import { SUPPORTED_TIME_ZONES } from './supported-time-zones';
import { TIME_ZONE_RENAMES } from './time-zone-renames';

// `description` is '' rather than absent for region-less zones (UTC, CET): a search key is never missing.
export type TimeZoneOption = {
  label: string;
  description: string;
  // spellings this option also answers to — the old name of a renamed city, never displayed
  searchTerms: string;
  value: string;
};

// Hermes (React Native) and jsdom do not implement supportedValuesOf. That used to leave the
// picker empty, which on mobile meant the field could never be set at all — so the shipped list
// is the fallback. It is the same set either way; the engine is only consulted to drop zones its
// tzdata does not carry, which would otherwise be offered and then fail to render a time.
const getSupportedTimeZones = (): string[] => {
  const zones = Array.from(SUPPORTED_TIME_ZONES.keys());
  if (typeof Intl.supportedValuesOf !== 'function') {
    return zones;
  }
  // Narrowed to the zones the backend will accept back. An engine lists every zone in its tzdata,
  // which is a superset: Microsoft Graph stores an event's zone as a Windows name and a few IANA
  // zones have no Windows equivalent, so offering one would only produce a save that fails.
  const engineZones = new Set(Intl.supportedValuesOf('timeZone'));
  return zones.filter((zone) => engineZones.has(zone));
};

// Also the display label outside the picker: a form row shows "Amsterdam", not "Europe/Amsterdam".
export const toCityLabel = (zone: string) =>
  zone.split('/').slice(-1)[0].replace(/_/g, ' ');

// A zone we do not ship has no country, so it falls back to the id's own continent segments.
const toDescription = (zone: string) =>
  SUPPORTED_TIME_ZONES.get(zone) ??
  zone
    .split('/')
    .slice(0, -1)
    .map((segment) => segment.replace(/_/g, ' '))
    .join(' / ');

// CLDR canonicalizes the other way (it lists Asia/Saigon), so `value` is often the old spelling.
const toOption = (
  canonical: string,
  zones: string[],
  pinned: Set<string>,
): TimeZoneOption => {
  const label = toCityLabel(canonical);

  return {
    label,
    description: SUPPORTED_TIME_ZONES.has(canonical)
      ? toDescription(canonical)
      : toDescription(zones[0]),
    searchTerms: zones
      .map(toCityLabel)
      .filter((city) => city !== label)
      .join(' '),
    // never rewrite the id the event already carries: the picker would stop matching its own value
    value:
      zones.find((zone) => pinned.has(zone)) ??
      zones.find((zone) => zone === canonical) ??
      zones[0],
  };
};

// `alwaysInclude` carries the event's own zone and the browser's: a provider can return a
// zone this engine does not list, and dropping it would silently reset the field on save.
// Deliberately NOT filtered against the supported set — these are values already in play, and
// hiding the one currently on the event would misrepresent it as unset.
export const getTimeZoneOptions = (
  alwaysInclude: (string | null | undefined)[] = [],
): TimeZoneOption[] => {
  const zones = new Set(getSupportedTimeZones());
  const pinned = new Set<string>();
  alwaysInclude.forEach((zone) => {
    if (!zone) {
      return;
    }
    zones.add(zone);
    pinned.add(zone);
  });

  // A renamed city is one place under two spellings, so its spellings collapse into one option.
  const groups = new Map<string, string[]>();
  zones.forEach((zone) => {
    const canonical = TIME_ZONE_RENAMES.get(zone) ?? zone;
    const group = groups.get(canonical);
    group ? group.push(zone) : groups.set(canonical, [zone]);
  });

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([canonical, grouped]) => toOption(canonical, grouped, pinned));
};
